import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import client, { imgUrl, logoUrl, assetUrl } from "@/lib/glossyApi";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  LayoutGrid, Users, Newspaper, Share2, Sparkles, Image as ImageIcon,
  Check, Trash2, RefreshCw, Send, X, LogOut, Plus, Pencil, Upload, Save, Eye, Megaphone, Star, PenLine, Zap, Gift, ArrowLeft, Tag, ExternalLink, Table2, Archive as ArchiveIcon, Clock, ChevronUp, ChevronDown, FileText, Globe,
} from "lucide-react";
import { LONDON_AREAS } from "@/lib/londonAreas";
import ShareKit from "@/components/ShareKit";
import WebsiteImport, { mergeProfile } from "@/components/WebsiteImport";
import { cycleIndexFor, editionLabel, editionStartIso, issueNoFor, upcomingEditions } from "@/lib/issue";

export const AD_SLOT_META = {
  home_band_a: { label: "Home · Feature Band A (top)", shape: "band", where: "Homepage — full-width band, upper page" },
  home_band_b: { label: "Home · Feature Band B (lower)", shape: "band", where: "Homepage — full-width band, lower page" },
  home_square: { label: "Home · Square tile", shape: "square", where: "Homepage — square tile in The Edit block" },
  home_strip: { label: "Home · Mid-page strip", shape: "banner", where: "Homepage — slim strip after The Interviews" },
  home_billboard: { label: "Home · Billboard (above footer)", shape: "band", where: "Homepage — the big band above the footer" },
  edit_band: { label: "The Edit · banner", shape: "banner", where: "The Edit page — slim strip under the header" },
  newsroom_banner: { label: "Newsroom · banner", shape: "banner", where: "Newsroom page — slim strip under the header" },
  interviews_band: { label: "Interviews · banner", shape: "banner", where: "Interviews page — slim strip under the header" },
  covermembers_banner: { label: "Cover Members · banner", shape: "banner", where: "Cover Members page — slim strip" },
  directory_banner: { label: "Directory · banner", shape: "banner", where: "Directory page — slim strip above the listings" },
  noticeboard_square: { label: "Noticeboard · banner", shape: "banner", where: "Noticeboard page — slim strip under the header" },
  desk_banner: { label: "Members' Desk · banner", shape: "banner", where: "Members' Desk page — slim strip" },
  article_square: { label: "Inside articles · square", shape: "square", where: "End of every article — square tile" },
  article_banner: { label: "Inside articles · banner", shape: "banner", where: "End of every article — slim strip below the square" },
  member_page_banner: { label: "Member profiles · banner", shape: "banner", where: "Every member profile page — slim strip near the foot" },
  archive_banner: { label: "The Archive · banner", shape: "banner", where: "Archive page — slim strip under the header" },
};

export const CATEGORIES = ["Money", "Business", "Culture", "Style", "Trends", "Marketing", "Workplace", "Community", "Success"];
export const SECTIONS = ["Culture", "Money", "The Edit", "Interview", "Cover Members", "Spotlight", "Members' Desk"];
export const TONES = ["Signature S/ME", "Witty & playful", "Punchy & bold", "Reflective", "Investigative", "Glamorous"];
export const LENGTHS = ["Short", "Standard", "Long"];
export const MCATS = ["Design Studio", "Bakery", "Tailoring", "Wine Bar", "Restaurant", "Cafe", "Salon", "Fitness", "Consulting", "Retail", "Photography", "Florist", "Other"];
export const BUSINESS_CATS = ["Food & Drink", "Beauty & Health", "Retail", "Online", "Services", "Arts & Entertainment"];

export function Header({ kicker, title, children }) {
  return (
    <div className="px-6 sm:px-10 py-8 border-b border-ink/15 flex flex-col sm:flex-row sm:items-end justify-between gap-4">
      <div>
        <p className="overline text-gloss-silver mb-2">{kicker}</p>
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tight">{title}</h1>
      </div>
      {children}
    </div>
  );
}


export function Stat({ label, value, sub }) {
  return (
    <div className="bg-cream p-5">
      <p className="font-serif text-3xl">{value ?? "—"}</p>
      <p className="overline text-ink/50 mt-1 truncate">{label}{sub ? ` · ${sub}` : ""}</p>
    </div>
  );
}

/* ---------------- Drip Calendar (all members at a glance) ---------------- */

export const InboxGroup = ({ title, note, testid, children }) => (
  <div className="mb-7" data-testid={testid}>
    <p className="overline text-gloss-gold">{title}</p>
    <p className="font-sans text-xs text-ink/50 mt-0.5 mb-3">{note}</p>
    <div className="border border-ink/15 divide-y divide-ink/10 bg-white">{children}</div>
  </div>
);
export const InboxRow = ({ main, sub, onOpen, testid }) => (
  <div className="flex items-center gap-4 px-5 py-3.5" data-testid={testid}>
    <div className="min-w-0 flex-1">
      <p className="font-serif text-lg truncate">{main}</p>
      <p className="font-sans text-xs text-ink/55 truncate">{sub}</p>
    </div>
    <button onClick={onOpen} className="overline border border-ink px-3 py-1.5 hover:bg-ink hover:text-cream transition-colors shrink-0">Open</button>
  </div>
);

export const DIn = ({ v, on, ph, testid, className = "", list }) => (
  <input value={v} onChange={(e) => on(e.target.value)} list={list} placeholder={ph} className={`bg-transparent border border-cream/30 px-4 py-3 font-sans focus:outline-none focus:border-gloss-gold ${className}`} data-testid={testid} />
);
export const DArea = ({ v, on, ph, testid, rows = 2 }) => (
  <textarea value={v} onChange={(e) => on(e.target.value)} placeholder={ph} rows={rows} className="w-full bg-transparent border border-cream/30 px-4 py-3 font-sans focus:outline-none focus:border-gloss-gold resize-none" data-testid={testid} />
);


export function Badge({ status }) {
  const map = { published: "bg-ink text-cream", review: "border border-gloss-silver text-ink", draft: "border border-ink/40 text-ink/60", onboarding: "border border-ink/40 text-ink/60", scheduled: "bg-gloss-gold text-ink", hidden: "border border-gloss-gold text-gloss-gold" };
  return <span className={`overline px-2.5 py-1 ${map[status] || "border border-ink/30"}`}>{status}</span>;
}

export function Panel({ title, children, testid }) {
  return (
    <div data-testid={testid}>
      <p className="overline text-gloss-silver border-b border-ink/15 pb-2 mb-4">{title}</p>
      {children}
    </div>
  );
}

export function Drawer({ children, onClose, title, wide }) {
  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="editor-drawer">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className={`relative w-full ${wide ? "max-w-2xl" : "max-w-lg"} bg-cream h-full overflow-y-auto`}>
        <div className="sticky top-0 bg-ink text-cream px-6 py-5 flex items-center justify-between z-20">
          <p className="font-serif text-2xl truncate">{title}</p>
          <button onClick={onClose} aria-label="Close" data-testid="close-drawer"><X size={22} /></button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

export function Field({ label, v, on, testid, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="overline text-ink/50">{label}</span>
      <input value={v} onChange={(e) => on(e.target.value)} data-testid={testid}
        className="w-full bg-transparent border-b border-ink/30 py-2.5 mt-1.5 font-sans text-base focus:outline-none focus:border-gloss-silver transition-colors" />
    </label>
  );
}
export function Area({ label, v, on, testid, rows = 3, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="overline text-ink/50">{label}</span>
      <textarea value={v} onChange={(e) => on(e.target.value)} rows={rows} data-testid={testid}
        className="w-full bg-transparent border border-ink/30 p-3 mt-1.5 font-sans text-base focus:outline-none focus:border-gloss-silver transition-colors resize-y" />
    </label>
  );
}
export function Select({ label, v, on, options, testid }) {
  return (
    <label className="block">
      <span className="overline text-ink/50">{label}</span>
      <select value={v} onChange={(e) => on(e.target.value)} data-testid={testid}
        className="w-full bg-transparent border-b border-ink/30 py-2.5 mt-1.5 font-sans text-base focus:outline-none focus:border-gloss-silver transition-colors">
        {options.map((o) => {
          const val = typeof o === "object" ? o.value : o;
          const lab = typeof o === "object" ? o.label : o;
          return <option key={val} value={val}>{lab}</option>;
        })}
      </select>
    </label>
  );
}
export function ComboField({ label, v, on, options, testid }) {
  const listId = `${testid}-list`;
  return (
    <label className="block">
      <span className="overline text-ink/50">{label}</span>
      <input value={v} onChange={(e) => on(e.target.value)} list={listId} data-testid={testid} placeholder="Type or pick an area"
        className="w-full bg-transparent border-b border-ink/30 py-2.5 mt-1.5 font-sans text-base focus:outline-none focus:border-gloss-silver transition-colors" />
      <datalist id={listId}>
        {options.map((o) => <option key={o} value={o} />)}
      </datalist>
    </label>
  );
}

